import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Search,
  Loader2,
  FileText,
  Pencil,
  HelpCircle,
  AlertCircle,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useTranslation } from "@/shared/i18n";
import { useCreatorPayments } from "../model/hooks";
import {
  CreatorPaymentStatus,
  type CreatorPayment,
} from "../types";
import { MockBanner } from "../ui/MockBanner";

const PAGE_SIZE = 10;

const currency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);

const formatDate = (iso?: string | null) => {
  if (!iso) return "—/—/————";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—/—/————";
    return format(d, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—/—/————";
  }
};

const formatReferenceMonth = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return format(d, "MMMM yyyy", { locale: ptBR });
  } catch {
    return iso;
  }
};

const statusConfig: Record<
  CreatorPaymentStatus,
  { label: string; icon: React.ElementType; className: string }
> = {
  [CreatorPaymentStatus.PAID]: {
    label: "PAGO",
    icon: FileText,
    className: "bg-emerald-600 hover:bg-emerald-600",
  },
  [CreatorPaymentStatus.SCHEDULED]: {
    label: "AGENDADO",
    icon: Pencil,
    className: "bg-blue-600 hover:bg-blue-600",
  },
  [CreatorPaymentStatus.PENDING]: {
    label: "AGUARDANDO",
    icon: HelpCircle,
    className: "bg-amber-600 hover:bg-amber-600",
  },
  [CreatorPaymentStatus.ERROR]: {
    label: "ERRO",
    icon: AlertCircle,
    className: "bg-destructive hover:bg-destructive",
  },
};

export function PaymentsHistoryPage() {
  const { t } = useTranslation("creator-financial");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<CreatorPaymentStatus | "all">("all");
  const [search, setSearch] = useState("");

  const { payments, totalItems, totalPages, isLoading } = useCreatorPayments({
    page,
    limit: PAGE_SIZE,
    status: statusFilter,
    search: search || undefined,
  });

  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalItems);

  if (isLoading && payments.length === 0) {
    return (
      <div className="creator-financial-page -m-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin opacity-45" />
      </div>
    );
  }

  const paymentBadgeClass: Record<CreatorPaymentStatus, string> = {
    [CreatorPaymentStatus.PAID]: "cf-badge-pill cf-badge-ativa",
    [CreatorPaymentStatus.SCHEDULED]: "cf-badge-pill cf-badge-default",
    [CreatorPaymentStatus.PENDING]: "cf-badge-pill cf-badge-inativa",
    [CreatorPaymentStatus.ERROR]: "cf-badge-pill bg-[rgba(239,68,68,0.2)] text-[var(--cf-danger)]",
  };

  return (
    <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
      <MockBanner />
      <div>
        <h1 className="cf-page-title">{t("payments.title")}</h1>
        <p className="cf-page-subtitle">{t("payments.subtitle")}</p>
      </div>

      <div className="cf-card">
        <div className="flex flex-row flex-wrap items-center justify-between gap-4 pb-4">
          <div className="flex flex-1 flex-wrap items-center gap-4 min-w-0">
            <div className="relative flex-1 max-w-sm min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--cf-text-muted)]" />
              <Input
                placeholder={t("payments.searchPlaceholder")}
                className="pl-9 bg-[var(--cf-surface-alt)] border-[var(--cf-border)] text-[var(--cf-text)] placeholder:text-[var(--cf-text-muted)]"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as CreatorPaymentStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px] bg-[var(--cf-surface-alt)] border-[var(--cf-border)] text-[var(--cf-text)]">
                <SelectValue placeholder={t("payments.allStatuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("payments.allStatuses")}</SelectItem>
                <SelectItem value={CreatorPaymentStatus.PAID}>
                  {t("payments.statusPaid")}
                </SelectItem>
                <SelectItem value={CreatorPaymentStatus.SCHEDULED}>
                  {t("payments.statusScheduled")}
                </SelectItem>
                <SelectItem value={CreatorPaymentStatus.PENDING}>
                  {t("payments.statusPending")}
                </SelectItem>
                <SelectItem value={CreatorPaymentStatus.ERROR}>
                  {t("payments.statusError")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="sm">
              {t("payments.filters")}
            </Button>
          </div>
          <Button size="sm">
            <Download className="mr-2 h-4 w-4" />
            {t("payments.export")}
          </Button>
        </div>
        <div>
          {payments.length === 0 ? (
            <p className="text-center py-12 text-[var(--cf-text-muted)]">
              {t("payments.empty")}
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--cf-border)] hover:bg-transparent">
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.statusColumn")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.valueBrl")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.paymentDate")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.referenceMonth")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.bank")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.accountPix")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("payments.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => {
                    const config =
                      statusConfig[payment.status] ?? statusConfig[CreatorPaymentStatus.PENDING];
                    const Icon = config.icon;
                    const badgeClass = paymentBadgeClass[payment.status];
                    return (
                      <TableRow key={payment.id} className="border-[var(--cf-border)]">
                        <TableCell>
                          <span className={`${badgeClass} inline-flex items-center gap-1`}>
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium text-[var(--cf-text)]">
                          {currency(payment.value)}
                        </TableCell>
                        <TableCell className="text-[var(--cf-text)]">
                          {payment.status === CreatorPaymentStatus.PENDING
                            ? "—/—/————"
                            : formatDate(payment.paymentDate)}
                        </TableCell>
                        <TableCell className="text-[var(--cf-text)]">
                          {formatReferenceMonth(payment.referencePaymentDate)}
                        </TableCell>
                        <TableCell className="text-[var(--cf-text)]">{payment.bankAccount?.bank ?? "—"}</TableCell>
                        <TableCell className="text-[var(--cf-text)]">
                          {payment.bankAccount ? (
                            <span className="text-sm text-[var(--cf-text-secondary)]">
                              Ag: **** CC: **** PIX: {payment.bankAccount.pix}
                            </span>
                          ) : (
                            <span className="text-[var(--cf-danger)]">
                              {t("payments.inactiveAccount")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {payment.status === CreatorPaymentStatus.PAID && (
                            <Button type="button" variant="ghost" size="icon">
                              <FileText className="h-4 w-4" />
                            </Button>
                          )}
                          {payment.status === CreatorPaymentStatus.SCHEDULED && (
                            <Button type="button" variant="ghost" size="icon">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {payment.status === CreatorPaymentStatus.PENDING && (
                            <Button type="button" variant="ghost" size="icon">
                              <HelpCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {payment.status === CreatorPaymentStatus.ERROR && (
                            <Button type="button" variant="destructive" size="sm">
                              {t("payments.fix")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between px-2 py-4 border-t border-[var(--cf-border)] mt-2">
                <p className="text-sm text-[var(--cf-text-muted)]">
                  {t("payments.showing", {
                    from: totalItems === 0 ? 0 : from,
                    to,
                    total: totalItems,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-[var(--cf-text-secondary)]">
                    {page} / {Math.max(1, totalPages)}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
